/**
 * CORE-904 — the bit of an S3 multipart upload that has to outlive the page.
 *
 * The transport already slices a large practice into parts and retries a failed
 * part in place, but everything it knows (`key`, `uploadId`, the part grid) lived
 * in a local variable: a reload, an app restart, or an error that escaped the
 * per-part retries dropped it, and the next attempt started from byte zero. That
 * left the ticket's second promise — «обрыв на 90% не означает заливку с нуля» —
 * unkept for exactly the case it was written for: multi-gigabyte files on a weak
 * channel.
 *
 * S3 itself is the source of truth for what has landed (`ListParts`, exposed as
 * `GET /videos/multipart/status`). All that has to survive locally is the handle
 * to the open upload plus enough to prove the bytes on disk are still the same
 * file. That is what this module persists.
 *
 * Two rules keep the parts from turning into a silent storage bill:
 *   - a record is adopted only by an upload of the *same* file (fingerprint) and
 *     only while it is fresh ({@link RESUME_TTL_MS});
 *   - every upload start aborts and drops the records that have aged out, so an
 *     abandoned upload is reaped by a later one instead of accumulating.
 *
 * Records are kept per file rather than one at a time, and only the *expired*
 * ones are reaped. That matters: a second upload started in another tab must not
 * abort the first one, which is exactly what a single-slot store would do to an
 * upload that is still running.
 */
import { storage } from '../utils/storage';

const STORE_KEY = 'agape_multipart_upload_v1';

/**
 * How long an interrupted upload stays resumable. A day covers «закрыл ноутбук,
 * вернулся утром» without keeping parts around indefinitely; S3 presigned URLs
 * expire in an hour but are re-minted on resume, so they don't bound this.
 */
export const RESUME_TTL_MS = 24 * 60 * 60 * 1000;

/** The handle to an open S3 multipart upload, as persisted between attempts. */
export interface ResumableUpload {
    /** Identity of the bytes — see {@link uploadFingerprint}. */
    fingerprint: string;
    key: string;
    uploadId: string;
    /** Which cloud the key lives in ('aws' | 'yandex'); the follow-up calls need it. */
    cloud: string;
    /** URL to commit once the object is assembled. */
    publicUrl: string;
    /** Part grid this upload was opened with — a resume must slice identically. */
    partSize: number;
    partCount: number;
    fileSizeBytes: number;
    /** Epoch ms of the original create, for the TTL. */
    createdAt: number;
}

/**
 * Identity of the file being uploaded, as far as the client can tell.
 *
 * `lastModified` is what makes this trustworthy on web, where the big uploads
 * happen: two different files matching on kind, name, size *and* mtime is not a
 * case worth engineering against. A native `{uri}` pick has no mtime, so there
 * the fingerprint is name + size — a resume onto a different file would need the
 * user to swap in another video with the same name and the same byte count.
 */
export function uploadFingerprint(kind: string, file: any, fileSizeBytes: number): string {
    const name = file?.name ?? file?.fileName ?? '';
    const mtime = typeof file?.lastModified === 'number' ? file.lastModified : '';
    return `${kind}|${name}|${fileSizeBytes}|${mtime}`;
}

/**
 * Whether a stored record may be adopted by the upload described by the other
 * arguments. Pure — the caller decides what to do with a `false` (abort + drop).
 */
export function canResume(
    record: ResumableUpload | null,
    expected: { fingerprint: string; partSize: number; partCount: number; fileSizeBytes: number },
    now: number,
): boolean {
    if (!record) return false;
    if (record.fingerprint !== expected.fingerprint) return false;
    // The grid must match byte-for-byte: parts already in S3 were cut with the
    // old one, and completing a mix of two grids assembles a corrupt object.
    if (record.partSize !== expected.partSize) return false;
    if (record.partCount !== expected.partCount) return false;
    if (record.fileSizeBytes !== expected.fileSizeBytes) return false;
    if (!Number.isFinite(record.createdAt)) return false;
    return now - record.createdAt <= RESUME_TTL_MS;
}

/** Part numbers still to upload, in order. */
export function missingParts(partCount: number, uploadedPartNumbers: number[]): number[] {
    const have = new Set(uploadedPartNumbers);
    const out: number[] = [];
    for (let n = 1; n <= partCount; n++) if (!have.has(n)) out.push(n);
    return out;
}

/**
 * Byte length of part `n` in the grid — the last part is the remainder. Used to
 * seed the progress bar with what S3 already holds, so a resumed upload doesn't
 * restart the percentage at zero.
 */
export function partByteLength(n: number, partSize: number, fileSizeBytes: number): number {
    if (n < 1 || partSize <= 0) return 0;
    const start = (n - 1) * partSize;
    if (start >= fileSizeBytes) return 0;
    return Math.min(partSize, fileSizeBytes - start);
}

/** The one this upload may adopt, if any. Pure. */
export function findResumable(
    records: ResumableUpload[],
    expected: { fingerprint: string; partSize: number; partCount: number; fileSizeBytes: number },
    now: number,
): ResumableUpload | null {
    return records.find((r) => canResume(r, expected, now)) ?? null;
}

/** Records nobody is going to finish — aborted and dropped at the next upload. Pure. */
export function expiredUploads(records: ResumableUpload[], now: number): ResumableUpload[] {
    return records.filter((r) => !Number.isFinite(r.createdAt) || now - r.createdAt > RESUME_TTL_MS);
}

/**
 * How many records are kept. One per file being uploaded; the cap only guards
 * against a store that grows forever if reaping never runs.
 */
const MAX_RECORDS = 10;

async function readAll(): Promise<Record<string, ResumableUpload>> {
    try {
        const raw = await storage.getJSON<Record<string, ResumableUpload>>(STORE_KEY);
        return raw && typeof raw === 'object' ? raw : {};
    } catch {
        return {};
    }
}

export async function loadResumableUploads(): Promise<ResumableUpload[]> {
    const all = await readAll();
    return Object.values(all).filter(
        (r) => r && typeof r.key === 'string' && typeof r.uploadId === 'string' && typeof r.fingerprint === 'string',
    );
}

export async function saveResumableUpload(record: ResumableUpload): Promise<void> {
    try {
        const all = await readAll();
        all[record.fingerprint] = record;
        const kept = Object.values(all)
            .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
            .slice(0, MAX_RECORDS);
        await storage.setJSON(STORE_KEY, Object.fromEntries(kept.map((r) => [r.fingerprint, r])));
    } catch {
        // A full or unavailable store costs the resume, not the upload.
    }
}

export async function clearResumableUpload(fingerprint: string): Promise<void> {
    try {
        const all = await readAll();
        if (!(fingerprint in all)) return;
        delete all[fingerprint];
        if (Object.keys(all).length === 0) await storage.removeJSON(STORE_KEY);
        else await storage.setJSON(STORE_KEY, all);
    } catch {
        /* nothing to do */
    }
}
